/**
 * Configurações de Estabilização do Chat8
 *
 * Este arquivo contém todas as configurações necessárias para garantir
 * a estabilidade e performance do sistema de chat.
 */

// Configurações de WebSocket
export const WEBSOCKET_CONFIG = {
  // URLs de conexão
  DEV_URL: "ws://localhost:8000/ws/chat",
  PROD_URL: "wss://licitarsv1.robovisorusa.com/ws/chat",

  // Configurações de reconexão
  MAX_RECONNECT_ATTEMPTS: 5,
  RECONNECT_DELAY: 1000,
  RECONNECT_BACKOFF_MULTIPLIER: 2,

  // Timeouts
  CONNECTION_TIMEOUT: 10000,
  MESSAGE_TIMEOUT: 30000,
  PING_INTERVAL: 30000,
  PONG_TIMEOUT: 10000,

  // Configurações de heartbeat
  HEARTBEAT_ENABLED: true,
  HEARTBEAT_INTERVAL: 25000,
  HEARTBEAT_TIMEOUT: 5000,
};

// Configurações de Sessão
export const SESSION_CONFIG = {
  // Validação de sessão
  SESSION_VALIDATION_TIMEOUT: 5000,
  MAX_SESSIONS_PER_USER: 10,
  SESSION_CLEANUP_INTERVAL: 300000, // 5 minutos

  // Configurações de persistência
  PERSIST_SESSION_DATA: true,
  SESSION_DATA_TTL: 24 * 60 * 60 * 1000, // 24 horas

  // Configurações de segurança
  SESSION_TOKEN_REFRESH_INTERVAL: 15 * 60 * 1000, // 15 minutos
  MAX_SESSION_DURATION: 8 * 60 * 60 * 1000, // 8 horas
};

// Configurações de Mensagens
export const MESSAGE_CONFIG = {
  // Limites de mensagens
  MAX_MESSAGE_LENGTH: 10000,
  MAX_MESSAGES_PER_SESSION: 1000,
  MESSAGE_RATE_LIMIT: 10, // mensagens por minuto

  // Configurações de cache
  MESSAGE_CACHE_SIZE: 100,
  MESSAGE_CACHE_TTL: 60 * 60 * 1000, // 1 hora

  // Configurações de retry
  MESSAGE_RETRY_ATTEMPTS: 3,
  MESSAGE_RETRY_DELAY: 1000,
  MESSAGE_RETRY_BACKOFF: 2,

  // Timeout para mensagens
  MESSAGE_TIMEOUT: 30000, // 30 segundos
};

// Configurações de Performance
export const PERFORMANCE_CONFIG = {
  // Debounce e throttle
  INPUT_DEBOUNCE_DELAY: 300,
  SCROLL_THROTTLE_DELAY: 100,
  RESIZE_THROTTLE_DELAY: 250,

  // Virtualização
  VIRTUALIZATION_ENABLED: true,
  VIRTUALIZATION_ITEM_HEIGHT: 80,
  VIRTUALIZATION_OVERSCAN: 5,

  // Lazy loading
  LAZY_LOAD_ENABLED: true,
  LAZY_LOAD_THRESHOLD: 0.1,
  LAZY_LOAD_DELAY: 100,
};

// Configurações de UI/UX
export const UI_CONFIG = {
  // Animações
  ANIMATION_DURATION: 200,
  ANIMATION_EASING: "ease-in-out",

  // Loading states
  LOADING_TIMEOUT: 10000,
  SKELETON_LOADING_ENABLED: true,

  // Feedback visual
  TOAST_DURATION: 4000,
  TOAST_POSITION: "top-right",

  // Responsividade
  MOBILE_BREAKPOINT: 768,
  TABLET_BREAKPOINT: 1024,
  DESKTOP_BREAKPOINT: 1280,
};

// Configurações de Error Handling
export const ERROR_CONFIG = {
  // Retry policies
  RETRY_ENABLED: true,
  MAX_RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,

  // Error boundaries
  ERROR_BOUNDARY_FALLBACK: true,
  ERROR_REPORTING_ENABLED: true,

  // Graceful degradation
  DEGRADATION_ENABLED: true,
  OFFLINE_MODE_ENABLED: true,
};

// Configurações de Monitoramento
export const MONITORING_CONFIG = {
  // Logging
  LOG_LEVEL: process.env.NODE_ENV === "production" ? "warn" : "debug",
  LOG_PERSISTENCE: true,
  LOG_MAX_ENTRIES: 1000,

  // Metrics
  METRICS_ENABLED: true,
  METRICS_INTERVAL: 60000, // 1 minuto

  // Health checks
  HEALTH_CHECK_INTERVAL: 30000, // 30 segundos
  HEALTH_CHECK_TIMEOUT: 5000,
};

// Configurações de Segurança
export const SECURITY_CONFIG = {
  // Validação de entrada
  INPUT_SANITIZATION_ENABLED: true,
  XSS_PROTECTION_ENABLED: true,

  // Autenticação
  TOKEN_REFRESH_ENABLED: true,
  TOKEN_REFRESH_THRESHOLD: 5 * 60 * 1000, // 5 minutos

  // Rate limiting
  RATE_LIMITING_ENABLED: true,
  RATE_LIMIT_WINDOW: 60000, // 1 minuto
  RATE_LIMIT_MAX_REQUESTS: 100,
};

// Configurações de Cache
export const CACHE_CONFIG = {
  // Cache de documentos
  DOCUMENT_CACHE_ENABLED: true,
  DOCUMENT_CACHE_TTL: 30 * 60 * 1000, // 30 minutos
  DOCUMENT_CACHE_MAX_SIZE: 50,

  // Cache de sessões
  SESSION_CACHE_ENABLED: true,
  SESSION_CACHE_TTL: 60 * 60 * 1000, // 1 hora
  SESSION_CACHE_MAX_SIZE: 20,

  // Cache de mensagens
  MESSAGE_CACHE_ENABLED: true,
  MESSAGE_CACHE_TTL: 15 * 60 * 1000, // 15 minutos
  MESSAGE_CACHE_MAX_SIZE: 200,
};

// Configurações de Sincronização
export const SYNC_CONFIG = {
  // Sincronização de estado
  STATE_SYNC_ENABLED: true,
  STATE_SYNC_INTERVAL: 5000, // 5 segundos

  // Sincronização de mensagens
  MESSAGE_SYNC_ENABLED: true,
  MESSAGE_SYNC_BATCH_SIZE: 10,
  MESSAGE_SYNC_INTERVAL: 2000, // 2 segundos

  // Resolução de conflitos
  CONFLICT_RESOLUTION_ENABLED: true,
  CONFLICT_RESOLUTION_STRATEGY: "last-write-wins",
};

// Configurações de Acessibilidade
export const ACCESSIBILITY_CONFIG = {
  // Navegação por teclado
  KEYBOARD_NAVIGATION_ENABLED: true,
  FOCUS_MANAGEMENT_ENABLED: true,

  // Screen readers
  SCREEN_READER_SUPPORT_ENABLED: true,
  ARIA_LABELS_ENABLED: true,

  // Contraste e cores
  HIGH_CONTRAST_MODE_ENABLED: true,
  COLOR_BLIND_FRIENDLY_MODE_ENABLED: true,
};

// Configurações de Internacionalização
export const I18N_CONFIG = {
  // Idiomas suportados
  SUPPORTED_LANGUAGES: ["pt-BR", "en-US"],
  DEFAULT_LANGUAGE: "pt-BR",

  // Fallback
  FALLBACK_LANGUAGE: "pt-BR",
  MISSING_TRANSLATION_FALLBACK: true,
};

// Configurações de Testes
export const TEST_CONFIG = {
  // Testes unitários
  UNIT_TESTS_ENABLED: true,
  UNIT_TEST_TIMEOUT: 5000,

  // Testes de integração
  INTEGRATION_TESTS_ENABLED: true,
  INTEGRATION_TEST_TIMEOUT: 10000,

  // Testes E2E
  E2E_TESTS_ENABLED: true,
  E2E_TEST_TIMEOUT: 30000,
};

// Configurações de Desenvolvimento
export const DEVELOPMENT_CONFIG = {
  // Hot reload
  HOT_RELOAD_ENABLED: process.env.NODE_ENV === "development",
  HOT_RELOAD_TIMEOUT: 1000,

  // Debug mode
  DEBUG_MODE_ENABLED: process.env.NODE_ENV === "development",
  DEBUG_LOGGING_ENABLED: process.env.NODE_ENV === "development",

  // Dev tools
  DEV_TOOLS_ENABLED: process.env.NODE_ENV === "development",
  REDUX_DEVTOOLS_ENABLED: process.env.NODE_ENV === "development",
};

// Configuração principal que combina todas as configurações
export const CHAT8_STABILIZATION_CONFIG = {
  websocket: WEBSOCKET_CONFIG,
  session: SESSION_CONFIG,
  message: MESSAGE_CONFIG,
  performance: PERFORMANCE_CONFIG,
  ui: UI_CONFIG,
  error: ERROR_CONFIG,
  monitoring: MONITORING_CONFIG,
  security: SECURITY_CONFIG,
  cache: CACHE_CONFIG,
  sync: SYNC_CONFIG,
  accessibility: ACCESSIBILITY_CONFIG,
  i18n: I18N_CONFIG,
  test: TEST_CONFIG,
  development: DEVELOPMENT_CONFIG,
};

// Função para obter configuração baseada no ambiente
export const getConfig = (
  environment: "development" | "production" = "development",
) => {
  const baseConfig = CHAT8_STABILIZATION_CONFIG;

  if (environment === "production") {
    return {
      ...baseConfig,
      websocket: {
        ...baseConfig.websocket,
        LOG_LEVEL: "warn",
      },
      development: {
        ...baseConfig.development,
        DEBUG_MODE_ENABLED: false,
        DEBUG_LOGGING_ENABLED: false,
        DEV_TOOLS_ENABLED: false,
        REDUX_DEVTOOLS_ENABLED: false,
      },
    };
  }

  return baseConfig;
};

// Função para validar configuração
export const validateConfig = (config: typeof CHAT8_STABILIZATION_CONFIG) => {
  const errors: string[] = [];

  // Validar configurações críticas
  if (config.websocket.MAX_RECONNECT_ATTEMPTS < 1) {
    errors.push("MAX_RECONNECT_ATTEMPTS deve ser maior que 0");
  }

  if (config.session.MAX_SESSIONS_PER_USER < 1) {
    errors.push("MAX_SESSIONS_PER_USER deve ser maior que 0");
  }

  if (config.message.MAX_MESSAGE_LENGTH < 1) {
    errors.push("MAX_MESSAGE_LENGTH deve ser maior que 0");
  }

  if (errors.length > 0) {
    throw new Error(`Configuração inválida: ${errors.join(", ")}`);
  }

  return true;
};

export default CHAT8_STABILIZATION_CONFIG;

export interface Chat8StabilizationConfig {
  enabled: boolean;
  maxRetries: number;
  retryDelay: number;
  timeout: number;
  debugMode: boolean;
}
